import { apiFetch, apiUpload } from '../api/client';
import { dataViaApi } from '../api/mode';
import { GraphError, graphFetch } from './client';
import { getSiteId } from './site';
import { LISTS } from '../data/listNames';

/**
 * AuditAttachments document library access (spec §5): one folder per
 * AuditId, files uploaded whole (≤500KB photos / ≤10MB PDFs — far under
 * Graph's 4MB simple-upload limit for photos; PDFs near the cap use the
 * same simple PUT, which allows up to ~250MB on drive items).
 */

let drivePromise: Promise<string> | null = null;

/** The library's drive id, resolved once per app lifetime. */
async function getAttachmentsDriveId(): Promise<string> {
  if (!drivePromise) {
    drivePromise = (async () => {
      const siteId = await getSiteId();
      const drive = await graphFetch<{ id: string }>(
        `/sites/${siteId}/lists/${LISTS.attachmentsLibrary}/drive?$select=id`,
      );
      return drive.id;
    })();
    drivePromise.catch(() => {
      drivePromise = null; // Allow retry after a transient failure.
    });
  }
  return drivePromise;
}

/** Create the folder if needed; an existing folder is success. */
async function ensureFolder(driveId: string, name: string): Promise<void> {
  try {
    await graphFetch(`/drives/${driveId}/root/children`, {
      method: 'POST',
      body: {
        name,
        folder: {},
        '@microsoft.graph.conflictBehavior': 'fail',
      },
    });
  } catch (err) {
    if (err instanceof GraphError && err.status === 409) return; // exists
    throw err;
  }
}

/**
 * Delete a whole folder (and its files) from the attachments library.
 * A missing folder is success — nothing was ever uploaded.
 */
export async function deleteAttachmentsFolder(folder: string): Promise<void> {
  if (dataViaApi()) {
    // The server only deletes task folders (deleting a task); audit files are never deleted.
    const task = /^Task-(\d+)$/.exec(folder);
    if (!task) throw new Error(`Only task folders can be deleted (${folder}).`);
    await apiFetch(`/tasks/${task[1]}/files/delete`, { method: 'POST', body: {}, write: true });
    return;
  }
  const driveId = await getAttachmentsDriveId();
  try {
    await graphFetch(`/drives/${driveId}/root:/${encodeURIComponent(folder)}:`, {
      method: 'DELETE',
    });
  } catch (err) {
    if (err instanceof GraphError && err.status === 404) return;
    throw err;
  }
}

export interface UploadedFile {
  /** The library LIST item id (for metadata + AuditId queries). */
  listItemId: number;
  /** Browser-openable SharePoint URL. */
  webUrl: string;
  /** The stored file name (in server mode the server names task files). */
  filename: string;
}

/**
 * Upload a file into `folder/filename` and stamp the library metadata
 * columns. Overwrites on same-name retry (idempotent for our deterministic
 * per-attachment filenames).
 */
export async function uploadToAttachmentsLibrary(
  folder: string,
  filename: string,
  blob: Blob,
  metadata: Record<string, unknown>,
  /** Task files in server mode: the key that makes a resend return the same file (B4 offline queue). */
  uploadKey?: string,
): Promise<UploadedFile> {
  if (dataViaApi()) return uploadViaServer(folder, filename, blob, metadata, uploadKey);
  const driveId = await getAttachmentsDriveId();
  await ensureFolder(driveId, folder);

  const path = `${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`;
  const item = await graphFetch<{ id: string; webUrl: string }>(
    `/drives/${driveId}/root:/${path}:/content?@microsoft.graph.conflictBehavior=replace`,
    { method: 'PUT', rawBody: blob },
  );

  // The file's list item carries the metadata columns.
  const listItem = await graphFetch<{ id: string }>(
    `/drives/${driveId}/items/${item.id}/listItem?$select=id`,
  );
  await graphFetch(
    `/drives/${driveId}/items/${item.id}/listItem/fields`,
    { method: 'PATCH', body: metadata },
  );

  return { listItemId: Number(listItem.id), webUrl: item.webUrl, filename };
}

/**
 * Server mode (Phase 6.5 step 3): the bytes go to /api/files; the server
 * checks the caller may add files to that audit/task, checks the content
 * really is a JPEG/PDF, and sets folder, store and uploader itself. Only
 * what it can't know is sent: parent, kind, name, question, sample entry.
 */
async function uploadViaServer(
  folder: string,
  filename: string,
  blob: Blob,
  metadata: Record<string, unknown>,
  uploadKey?: string,
): Promise<UploadedFile> {
  const audit = /^Audit-(\d+)$/.exec(folder);
  const task = /^Task-(\d+)$/.exec(folder);
  if (!audit && !task) throw new Error(`Unexpected attachments folder ${folder}.`);
  const params = new URLSearchParams({
    target: audit ? 'audit' : 'task',
    id: (audit ?? task)![1]!,
    kind: metadata.AttachmentType === 'pdf' ? 'pdf' : 'photo',
    name: filename,
  });
  if (typeof metadata.QuestionId === 'string' && metadata.QuestionId) params.set('questionId', metadata.QuestionId);
  if (typeof metadata.SampleIndex === 'number') params.set('sampleIndex', String(metadata.SampleIndex));
  if (task && uploadKey) params.set('key', uploadKey);
  return apiUpload<UploadedFile>(`/files?${params.toString()}`, blob);
}
